import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Eye, Calendar, Tag, CheckCircle, FileText, Palette, Code, User, Download, XCircle, ExternalLink, Play, Layers } from 'lucide-react';
import { Component } from '../types';
import { useProjects } from '../hooks/useApi';
import { componentsApi } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

import BookmarkButton from '../components/BookmarkButton';
import { getComponentTypeColor, getStatusColor, getAccessibilityFeatures, formatDate } from '../utils/filters';

const ComponentDetail: React.FC = () => {
  const { componentName, projectId } = useParams<{ componentName: string; projectId: string }>();
  const [component, setComponent] = useState<Component | null>(null);
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use cached hook instead of direct API call
  const { data: allProjects, loading: projectsLoading, error: projectsError } = useProjects();

  useEffect(() => {
    if (allProjects && componentName && projectId) {
      const findComponent = async () => {
        try {
          setLoading(true);
          
          // Find the specific project first
          const targetProject = allProjects.find(p => p.id === projectId || p.name === projectId);
          if (!targetProject) {
            setError(`Project ${projectId} not found`);
            setLoading(false);
            return;
          }
          
          // Find the component in the project's components
          const projectComponents = await componentsApi.getByProjectId(targetProject.id);
          const foundComponent = projectComponents.find((c: Component) => c.name === componentName);
          
          if (!foundComponent) {
            setError(`Component ${componentName} not found in project ${targetProject.name}`);
            setLoading(false);
            return;
          }
          
          setComponent(foundComponent);
          setProject(targetProject);
          setError(null);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to load component');
        } finally {
          setLoading(false);
        }
      };

      findComponent();
    }
  }, [allProjects, componentName, projectId]);

  // Handle errors from cached hooks
  useEffect(() => {
    if (projectsError) {
      setError(projectsError);
    }
  }, [projectsError]);

  // Combine loading states
  const isLoading = loading || projectsLoading;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !component) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Error Loading Component</h1>
          <p className="text-slate-600 dark:text-slate-300 mb-4">{error || 'Component not found'}</p>
          <Link to="/" className="btn-primary">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const accessibilityFeatures = getAccessibilityFeatures(component);


  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <Link 
              to={`/projects/${projectId}`} 
              className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Project
            </Link>
            <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <Link 
                to="/" 
                className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
              >
                Dashboard
              </Link>
              <span className="text-gray-400">/</span>
              <Link 
                to={`/projects/${projectId}`} 
                className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
              >
                {component?.projectName || projectId}
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-900 dark:text-white font-medium">{componentName}</span>
            </nav>
          </div>
        </div>

        {/* Component Header */}
        <div className="card liquid-glass mb-8 border-0 shadow-lg p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-4">
                <h1 className="text-4xl font-bold gradient-text">
                  {component.name}
                </h1>
                <span className={`badge ${getComponentTypeColor(component.type || 'unknown')} text-sm px-3 py-1`}>
                  {component.type || 'unknown'}
                </span>
                <span className={`badge ${getStatusColor(component.status || 'draft')} text-sm px-3 py-1`}>
                  {component.status || 'draft'}
                </span>
              </div>
              
              {component.description && (
                <p className="text-lg text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">{component.description}</p>
              )}

              <div className="flex items-center space-x-6 text-sm text-slate-500 dark:text-slate-400">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>Updated {formatDate(component.lastUpdated || project?.lastUpdated)}</span>
                </div>
                {component.author && (
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4" />
                    <span>{component.author}</span>
                  </div>
                )}
              </div>
              
              {/* README Note */}
              {!component.notes && (
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <div className="flex items-center space-x-2 text-amber-800 dark:text-amber-200">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm font-medium">No README or documentation available for this component</span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3 mt-6 lg:mt-0">
              <BookmarkButton component={component} project={project} size="md" />
              {component.previewUrl && (
                <a
                  href={(() => {
                    // Combine project.patternlabUrl + component.previewUrl
                    const projectPatternlabUrl = project?.patternlabUrl || '';
                    const componentPreviewUrl = component.previewUrl || '';
                    
                    if (componentPreviewUrl.startsWith('http')) {
                      // If component.previewUrl is already a full URL, use it directly
                      return componentPreviewUrl;
                    } else if (projectPatternlabUrl && componentPreviewUrl) {
                      // Combine project.patternlabUrl + component.previewUrl
                      const baseUrl = projectPatternlabUrl.replace(/\/$/, ''); // Remove trailing slash
                      const previewPath = componentPreviewUrl.replace(/^\//, ''); // Remove leading slash
                      return `${baseUrl}/${previewPath}`;
                    } else if (projectPatternlabUrl) {
                      // If component.previewUrl is empty, use project.patternlabUrl only
                      return projectPatternlabUrl;
                    } else {
                      return '#';
                    }
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary flex items-center space-x-2 shadow-md hover:shadow-lg transition-all duration-200"
                >
                  <Play className="w-4 h-4" />
                  <span>Preview</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Component Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Variants - Redesigned */}
            {component.variants && component.variants.length > 0 && (
              <div className="card liquid-glass border-0 shadow-lg p-6">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-center">
                  <Layers className="w-6 h-6 mr-3 text-primary-600" />
                  Variants ({component.variants.length})
                </h3>
                <div className="space-y-4">
                  {component.variants.map((variant, index) => (
                    <a
                      key={`${variant.name}-${index}`}
                      href={(() => {
                        const projectPatternlabUrl = project?.patternlabUrl || '';
                        const variantPreviewUrl = variant.previewUrl || '';
                        
                        if (variantPreviewUrl.startsWith('http')) {
                          return variantPreviewUrl;
                        } else if (projectPatternlabUrl && variantPreviewUrl) {
                          const baseUrl = projectPatternlabUrl.replace(/\/$/, '');
                          const previewPath = variantPreviewUrl.replace(/^\//, '');
                          return `${baseUrl}/${previewPath}`;
                        } else if (projectPatternlabUrl) {
                          return projectPatternlabUrl;
                        } else {
                          return '#';
                        }
                      })()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative bg-white/50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.01] backdrop-blur-sm block cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                            <h4 className="font-semibold text-slate-900 dark:text-white text-lg group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {variant.name}
                            </h4>
                          </div>
                          {variant.description && (
                            <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                              {variant.description}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          <div className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-md group-hover:scale-105">
                            <Eye className="w-4 h-4" />
                            <span>Preview</span>
                            <ExternalLink className="w-3 h-3" />
                          </div>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {component.notes && (
              <div className="card liquid-glass border-0 shadow-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <FileText className="w-6 h-6 mr-3 text-primary-600" />
                  Notes
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {component.notes}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Tags - Dedicated section */}
            <div className="card liquid-glass border-0 shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                <Tag className="w-5 h-5 mr-2 text-primary-600" />
                Tags
              </h3>
              {component.tags && component.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {component.tags.map((tag: string) => (
                    <span
                      key={tag}
                      className="bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-800 dark:text-blue-200 px-3 py-2 rounded-full text-sm font-medium border border-blue-200 dark:border-blue-700 hover:shadow-sm transition-all duration-200"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 dark:text-gray-400 text-sm italic">
                  No tags available for this component
                </div>
              )}
            </div>

            {/* Accessibility Features */}
            {accessibilityFeatures.length > 0 && (
              <div className="card liquid-glass border-0 shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-success-600" />
                  Accessibility Features
                </h3>
                <div className="space-y-3">
                  {accessibilityFeatures.map(({ feature, value }) => (
                    <div
                      key={feature}
                      className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700"
                    >
                      {value ? (
                        <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      )}
                      <span className={`capitalize font-medium ${value ? 'text-gray-700 dark:text-gray-300' : 'text-red-600 dark:text-red-400'}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentDetail;
